/**
 * DisplayPopover — controls board display options: sort, grouping, card field visibility.
 * Native popover with click-outside dismiss. No external UI library dependencies.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  SlidersHorizontal,
  X,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Plus,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  type SortField,
  type GroupField,
  type CardField,
  SORT_FIELD_LABELS,
  GROUP_FIELD_LABELS,
  CARD_FIELD_LABELS,
  ALL_CARD_FIELDS,
} from './displayTypes';
import { type UseBoardDisplayReturn } from '../../../hooks/useBoardDisplay';

// ── Color tokens ──
// Brand-aware: each value is a CSS expression reading the active CSS var
// (set by AppearanceProvider) with a Martha hex fallback.

const colors = {
  text: 'rgb(var(--c-text-default, 47 36 27))',
  textSecondary: 'rgb(var(--c-text-subtle, 107 93 82))',
  textMuted: 'rgb(var(--c-text-faint, 163 150 134))',
  bg: 'rgb(var(--c-surface-default, 255 255 255))',
  bgOffWhite: 'rgb(var(--c-surface-bright, 253 252 250))',
  border: 'rgb(var(--c-border-default, 232 224 213))',
  coral: 'rgb(var(--c-coral-500, 217 127 111))',
  coralHover: 'rgb(var(--c-coral-600, 199 110 94))',
  inputBg: 'rgb(var(--c-surface-subtle, 245 241 236))',
} as const;

// ── Sortable fields (excludes 'manual') ──

const SORTABLE_FIELDS: SortField[] = [
  'priority', 'status', 'created', 'updated', 'due_date', 'title', 'type', 'story_points', 'completion',
];

// ── Props ──

interface DisplayPopoverProps {
  display: UseBoardDisplayReturn;
}

// ── Toggle Switch (inline, no deps) ──

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        position: 'relative',
        width: 36,
        height: 20,
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        backgroundColor: checked ? colors.coral : colors.border,
        transition: 'background-color 0.15s ease',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          transition: 'left 0.15s ease',
        }}
      />
    </button>
  );
}

// ── Small Select Dropdown ──

function MiniSelect<T extends string>({
  value,
  options,
  labels,
  onChange,
  style,
}: {
  value: T;
  options: T[];
  labels: Record<T, string>;
  onChange: (v: T) => void;
  style?: React.CSSProperties;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      style={{
        appearance: 'none',
        WebkitAppearance: 'none',
        background: `${colors.inputBg} url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23A39686' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
        padding: '5px 26px 5px 10px',
        fontSize: 13,
        color: colors.text,
        cursor: 'pointer',
        outline: 'none',
        lineHeight: '18px',
        ...style,
      }}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>{labels[opt]}</option>
      ))}
    </select>
  );
}

// ── Section Header ──

function SectionHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: colors.textMuted,
        padding: '12px 16px 6px',
      }}
    >
      {label}
    </div>
  );
}

// ── Main Component ──

export function DisplayPopover({ display }: DisplayPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const {
    sortRules,
    addSortRule,
    removeSortRule,
    updateSortRule,
    isManualSort,
    visibleFields,
    toggleField,
    isFieldVisible,
    groupBy,
    setGroupBy,
    resetToDefaults,
    hasCustomSettings,
  } = display;

  // Compute which sort fields are already in use
  const usedSortFields = new Set(sortRules.map((r) => r.field));
  const availableSortFields = SORTABLE_FIELDS.filter((f) => !usedSortFields.has(f));

  const handleAddSort = useCallback(() => {
    if (availableSortFields.length > 0) {
      addSortRule(availableSortFields[0], 'asc');
    }
  }, [availableSortFields, addSortRule]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* ── Trigger Button ── */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          border: `1px solid ${isOpen ? colors.coral : colors.border}`,
          borderRadius: 8,
          background: isOpen ? colors.bgOffWhite : colors.bg,
          color: isOpen ? colors.coral : colors.textSecondary,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
          lineHeight: '20px',
          transition: 'all 0.15s ease',
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = colors.coral;
            e.currentTarget.style.color = colors.coral;
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.color = colors.textSecondary;
          }
        }}
      >
        <SlidersHorizontal size={15} strokeWidth={2} />
        <span className="display-popover-label" style={{ display: 'none' }}>Display</span>
        {hasCustomSettings && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors.coral,
              border: `2px solid ${colors.bg}`,
            }}
          />
        )}
      </button>

      {/* ── Show "Display" text on wider viewports via CSS ── */}
      <style>{`
        @media (min-width: 640px) {
          .display-popover-label { display: inline !important; }
        }
      `}</style>

      {/* ── Popover Panel ── */}
      {isOpen && (
        <div
          data-testid="display-popover-panel"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 300,
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            boxShadow: '0 8px 30px rgb(var(--c-shadow-color, 47 36 27) / 0.12), 0 2px 8px rgb(var(--c-shadow-color, 47 36 27) / 0.06)',
            zIndex: 50,
          }}
        >
          {/* ── Sort Section ── */}
          <SectionHeader label="Sort" />

          {isManualSort ? (
            <div style={{ padding: '4px 16px 12px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: colors.inputBg,
                  borderRadius: 8,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                <span style={{ flex: 1 }}>Manual ordering</span>
                <span style={{ fontSize: 11, color: colors.textMuted }}>Drag to reorder</span>
              </div>
              <button
                type="button"
                onClick={handleAddSort}
                disabled={availableSortFields.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                  padding: '4px 0',
                  border: 'none',
                  background: 'none',
                  color: availableSortFields.length > 0 ? colors.coral : colors.textMuted,
                  fontSize: 13,
                  cursor: availableSortFields.length > 0 ? 'pointer' : 'default',
                  fontWeight: 500,
                }}
              >
                <Plus size={14} strokeWidth={2} />
                Add sort
              </button>
            </div>
          ) : (
            <div style={{ padding: '4px 16px 8px' }}>
              {sortRules.map((rule, idx) => (
                <div
                  key={`${rule.field}-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  {/* Field selector */}
                  <MiniSelect<SortField>
                    value={rule.field}
                    options={[rule.field, ...availableSortFields].filter(
                      (f): f is SortField => f !== 'manual'
                    )}
                    labels={SORT_FIELD_LABELS}
                    onChange={(field) => updateSortRule(idx, { field })}
                    style={{ flex: 1, minWidth: 0 }}
                  />

                  {/* Direction toggle */}
                  <button
                    type="button"
                    onClick={() =>
                      updateSortRule(idx, {
                        direction: rule.direction === 'asc' ? 'desc' : 'asc',
                      })
                    }
                    title={rule.direction === 'asc' ? 'Ascending' : 'Descending'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 30,
                      height: 30,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 6,
                      background: colors.inputBg,
                      cursor: 'pointer',
                      color: colors.textSecondary,
                      flexShrink: 0,
                    }}
                  >
                    {rule.direction === 'asc' ? (
                      <ArrowUp size={14} strokeWidth={2} />
                    ) : (
                      <ArrowDown size={14} strokeWidth={2} />
                    )}
                  </button>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => removeSortRule(idx)}
                    title="Remove sort"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 30,
                      height: 30,
                      border: 'none',
                      borderRadius: 6,
                      background: 'none',
                      cursor: 'pointer',
                      color: colors.textMuted,
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = colors.coral;
                      e.currentTarget.style.backgroundColor = colors.inputBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.textMuted;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </div>
              ))}

              {/* Add sort button */}
              <button
                type="button"
                onClick={handleAddSort}
                disabled={availableSortFields.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 4,
                  padding: '4px 0',
                  border: 'none',
                  background: 'none',
                  color: availableSortFields.length > 0 ? colors.coral : colors.textMuted,
                  fontSize: 13,
                  cursor: availableSortFields.length > 0 ? 'pointer' : 'default',
                  fontWeight: 500,
                }}
              >
                <Plus size={14} strokeWidth={2} />
                Add sort
              </button>
            </div>
          )}

          {/* ── Divider ── */}
          <div style={{ height: 1, backgroundColor: colors.border, margin: '0 16px' }} />

          {/* ── Group By Section ── */}
          <SectionHeader label="Group by" />

          <div style={{ padding: '4px 16px 12px' }}>
            <MiniSelect<GroupField>
              value={groupBy}
              options={(['none', 'status', 'priority', 'type', 'assignee'] as GroupField[])}
              labels={GROUP_FIELD_LABELS}
              onChange={setGroupBy}
              style={{ width: '100%' }}
            />
          </div>

          {/* ── Divider ── */}
          <div style={{ height: 1, backgroundColor: colors.border, margin: '0 16px' }} />

          {/* ── Card Fields Section ── */}
          <SectionHeader label="Card fields" />

          <div style={{ padding: '4px 16px 12px' }}>
            {ALL_CARD_FIELDS.map((field) => {
              const visible = isFieldVisible(field);
              return (
                <div
                  key={field}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: visible ? colors.text : colors.textMuted,
                    }}
                  >
                    {visible ? (
                      <Eye size={14} strokeWidth={1.5} style={{ color: colors.textMuted }} />
                    ) : (
                      <EyeOff size={14} strokeWidth={1.5} style={{ color: colors.textMuted }} />
                    )}
                    {CARD_FIELD_LABELS[field]}
                  </div>
                  <ToggleSwitch
                    checked={visible}
                    onChange={() => toggleField(field)}
                  />
                </div>
              );
            })}
          </div>

          {/* ── Footer ── */}
          {hasCustomSettings && (
            <>
              <div style={{ height: 1, backgroundColor: colors.border, margin: '0 16px' }} />
              <div style={{ padding: '10px 16px 12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    resetToDefaults();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    border: 'none',
                    background: 'none',
                    color: colors.textMuted,
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: '4px 0',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.coral;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = colors.textMuted;
                  }}
                >
                  <RotateCcw size={13} strokeWidth={2} />
                  Reset to defaults
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DisplayPopover;
